import { ISlotFinding, IValidationInput, IValidationOutput, IValidator } from "../types";
import { OVD_BOUNDS, STAF_INTERMEDIATE_SLOT_FIELDS } from "./consts";
import {
  isContainerLengthKey,
  isPlainObject,
  isYesNo,
  parseSlotKey,
  tierWithinLevel,
} from "./patterns";

import { BayLevelEnum } from "open-vessel-definition";
import { createValidationOutput } from "../helpers/createValidationOutput";

const name = "slotStructureValidator";
const okTranslationKey = "validators:structure.slotStructure.ok";
const errorTranslationKey = "validators:structure.slotStructure.error";

export type TSlotStructureCode =
  /** The key is not `RRTT` or `RRTTT` */
  | "badPattern"
  /** The tier is odd */
  | "badParity"
  | "rowOutOfBounds"
  /** The tier is outside its level's range (below 02–66, above 68–120) */
  | "tierOutOfLevel"
  /** `pos` differs from the key. Fixable */
  | "posMismatch"
  | "wrongType"
  /** `sizes` is missing. Fixable: becomes `{}` with `restricted: 1` */
  | "missing"
  /** A `sizes` key is not a container length */
  | "badSize"
  | "badEnum"
  /** `restricted: 1` together with accepted sizes */
  | "restrictedWithSizes"
  /** No accepted size and not `restricted`. Fixable: `restricted: 1` */
  | "emptyNotRestricted"
  /** A field that only exists while converting from STAF. Fixable */
  | "leakedField";

export interface ISlotStructureFinding extends ISlotFinding {
  code: TSlotStructureCode;
  /** Relative to the slot: `""` for the key itself, `sizes.40`, `reefer`, … */
  path: string;
}

export interface ISlotStructureOutput {
  findings: ISlotStructureFinding[];
  slotCount: number;
}

export const FIXABLE_SLOT_CODES: readonly TSlotStructureCode[] = [
  "posMismatch",
  "missing",
  "emptyNotRestricted",
  "leakedField",
];

/**
 * Every slot of every Bay-level: the key pattern and bounds, the tier's parity and its
 * level, `pos` against the key, the `sizes` dictionary, the `0 | 1` flags, and the two
 * contradictions (restricted with sizes, empty without `restricted`).
 *
 * One pass over the slots, nothing allocated per slot but its findings. Never throws.
 */
export const slotStructureValidator: IValidator<ISlotStructureOutput> = {
  name,
  okTranslationKey,
  errorTranslationKey,
  fn: slotStructureValidatorFn,
  severity: "error",
  stage: "structure",
};

export function slotStructureValidatorFn({
  data: { json },
}: IValidationInput): IValidationOutput<ISlotStructureOutput> {
  const findings: ISlotStructureFinding[] = [];
  const baysData: unknown[] = Array.isArray(json?.baysData) ? json.baysData : [];
  let slotCount = 0;

  baysData.forEach((entry) => {
    if (!isPlainObject(entry)) return;

    const isoBay = typeof entry["isoBay"] === "string" ? entry["isoBay"] : "";
    const level = entry["level"] as BayLevelEnum;
    const perSlotInfo = entry["perSlotInfo"];

    if (perSlotInfo === undefined) return;
    if (!isPlainObject(perSlotInfo)) {
      findings.push({ code: "wrongType", path: "perSlotInfo", isoBay, level, pos: "" });
      return;
    }

    for (const pos in perSlotInfo) {
      slotCount++;
      const flag = (code: TSlotStructureCode, path: string) =>
        findings.push({ code, path, isoBay, level, pos });

      const parsed = parseSlotKey(pos);
      if (parsed === undefined) flag("badPattern", "");
      else {
        if (parsed.tierN % 2 !== 0) flag("badParity", "");
        if (parsed.rowN > OVD_BOUNDS.ROW_MAX) flag("rowOutOfBounds", "");
        if (!tierWithinLevel(parsed.tierN, level)) flag("tierOutOfLevel", "");
      }

      const slot = perSlotInfo[pos];
      if (!isPlainObject(slot)) {
        flag("wrongType", "");
        continue;
      }

      if (slot["pos"] !== pos) flag("posMismatch", "pos");

      checkSizesAndFlags(slot, flag);

      STAF_INTERMEDIATE_SLOT_FIELDS.forEach((f) => {
        if (slot[f] !== undefined) flag("leakedField", f);
      });
    }
  });

  return createValidationOutput(slotStructureValidator, findings, { findings, slotCount });
}

type TFlag = (code: TSlotStructureCode, path: string) => void;

function checkSizesAndFlags(slot: Record<string, unknown>, flag: TFlag): void {
  (["reefer", "coolStowProhibited", "restricted"] as const).forEach((f) => {
    if (slot[f] !== undefined && !isYesNo(slot[f])) flag("badEnum", f);
  });

  const hz = slot["hazardousProhibited"];
  if (hz !== undefined && hz !== true && !isPlainObject(hz)) flag("badEnum", "hazardousProhibited");

  const sizes = slot["sizes"];
  if (sizes === undefined) return flag("missing", "sizes");
  if (!isPlainObject(sizes)) return flag("wrongType", "sizes");

  let accepted = 0;
  for (const k in sizes) {
    if (!isContainerLengthKey(k)) {
      flag("badSize", `sizes.${k}`);
      continue;
    }
    const v = sizes[k];
    if (v === 1) accepted++;
    else if (isPlainObject(v)) {
      accepted++;
      if (v["cone"] !== undefined && !isYesNo(v["cone"])) flag("badEnum", `sizes.${k}.cone`);
    } else if (v !== 0) flag("badEnum", `sizes.${k}`);
  }

  if (slot["restricted"] === 1 && accepted > 0) flag("restrictedWithSizes", "restricted");
  if (accepted === 0 && slot["restricted"] !== 1) flag("emptyNotRestricted", "sizes");
}
